import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { siteStats, testimonials } from "@/mocks/content";

const principles = [
  {
    icon: "ri-scales-3-line",
    title: "不站队，只摆事实",
    desc: "我们更愿意把已知事实、合理推断与个人看法分开写，让读者自己完成判断，而不是替读者下结论。",
  },
  {
    icon: "ri-shield-user-line",
    title: "脱敏化处理",
    desc: "所有案例均以类型化事件为样本，不指向现实中的具体个人，讨论的是传播机制与舆论逻辑。",
  },
  {
    icon: "ri-time-line",
    title: "先核实，再动笔",
    desc: "宁可晚一天发布，也要等信息足够再写。缺乏可核验来源的爆料，我们一般不采用。",
  },
  {
    icon: "ri-eye-2-line",
    title: "承认视角的局限",
    desc: "任何解读都带有观察角度。我们会在文中标注不确定的部分，而不是把推测写成定论。",
  },
];

const milestones = [
  { year: "2023", title: "从一条时间线开始", desc: "第一篇解读只做了一件事：把散落的时间点排成一条线。读者的反馈让我们确认，整理比表态更有价值。" },
  { year: "2024", title: "建立五个固定栏目", desc: "深扒解读、幕后逻辑、人物关系、细节复盘、舆论观察陆续成形，形成稳定的分析框架。" },
  { year: "2025", title: "把方法讲清楚", desc: "开始系统整理「如何吃瓜」的方法论内容，帮助读者建立自己的信息核验习惯。" },
  { year: "2026", title: "持续跟进，保持克制", desc: "更新频率提升到每周五篇，但内容准则没有改变：宁可少写，也不写没有依据的猜测。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 黑料吃话内容理念与团队介绍",
    description:
      "了解黑料吃话的定位、发展理念与内容准则。我们专注娱乐热点的深度解读与舆论观察，坚持脱敏化处理、先核实再动笔，帮助读者建立自己的判断方法。",
    keywords: "黑料吃话,关于我们,黑料吃话介绍,内容准则,舆论观察,深度解读",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料吃话",
        description: "黑料吃话的品牌介绍、发展理念与内容准则",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "黑料吃话",
        email: "contact@heiliaochihua.example",
        description: "以深扒解读为核心的娱乐热点评论内容站",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${window.location.origin}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${window.location.origin}/about` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <main className="flex-1">
        <section className="w-full bg-background-100 py-12 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="text-xs font-medium tracking-[0.24em] text-primary-600">ABOUT US</span>
                <h1 className="mt-3 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  关于黑料吃话
                </h1>
                <p className="mt-5 text-sm leading-8 text-foreground-600 md:text-base">
                  「黑料吃话」是一个专注娱乐热点深度解读的内容站。我们不追逐爆料的第一时间，而是把散落的传闻、声明、路透与舆论反馈整理成可复盘的时间线，从细节入手分析事件的幕后逻辑与人物关系。
                </p>
                <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-base">
                  我们希望提供的不只是一个结论，而是一套观察问题的方法——让读者在信息碎片里，依然能看清事件的轮廓。
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <Link
                    to="/topics"
                    className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-book-shelf-line"></i>浏览话题库
                  </Link>
                  <Link
                    to="/contact"
                    className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i className="ri-mail-line"></i>联系我们
                  </Link>
                </div>
              </div>
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20workspace%20with%20open%20paper%20notebooks%2C%20timeline%20sticky%20notes%20and%20vintage%20desk%20lamp%2C%20terracotta%20and%20cream%20palette%2C%20soft%20natural%20light%2C%20minimal%20stylized%20still%20life%2C%20no%20text&width=1200&height=900&seq=hlch-about-main&orientation=landscape"
                  alt="暖色调工作台与时间线笔记，象征黑料吃话的整理与解读方式"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-background-200 md:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="flex flex-col items-center bg-background-100 px-4 py-8">
                  <i className={`${stat.icon} text-2xl text-primary-600`}></i>
                  <span className="mt-3 font-heading text-2xl font-bold text-foreground-950">{stat.value}</span>
                  <span className="mt-1 text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-medium tracking-[0.24em] text-primary-600">POSITIONING</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们的定位</h2>
              <p className="mt-5 text-sm leading-8 text-foreground-600">
                我们把自己定位为「热点翻译器」：把行业内部才懂的规则、含糊的措辞与分散的材料，翻译成普通读者能读懂的分析。我们不生产情绪，也不制造对立，只负责把线索摆整齐。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100">
                    <i className={`${item.icon} text-2xl text-secondary-700`}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-medium tracking-[0.24em] text-primary-600">JOURNEY</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展历程</h2>
            </div>
            <ol className="mt-10 space-y-6 border-l border-background-300 pl-6 md:pl-8">
              {milestones.map((item) => (
                <li key={item.year} className="relative">
                  <span className="absolute -left-[31px] top-2 flex h-3 w-3 items-center justify-center rounded-full bg-accent-500 md:-left-[39px]"></span>
                  <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                    <span className="font-heading text-sm font-bold text-primary-600">{item.year}</span>
                    <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-medium tracking-[0.24em] text-primary-600">VOICES</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者的口碑</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((item) => (
                <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <blockquote className="mt-4 flex-1 text-sm leading-8 text-foreground-700">{item.text}</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-background-200 pt-5">
                    <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-background-200">
                      <img src={item.avatar} alt={`${item.name}的头像`} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="flex flex-col">
                      <span className="font-heading text-sm font-bold text-foreground-900">{item.name}</span>
                      <span className="text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-7 md:p-10">
              <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">与我们保持联系</h2>
                  <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
                    无论是提供线索、内容合作还是纠错反馈，都欢迎来信。我们通常在两个工作日内回复。
                  </p>
                  <p className="mt-3 text-sm text-foreground-700">
                    邮箱：
                    <a
                      href="mailto:contact@heiliaochihua.example"
                      className="text-primary-700 underline-offset-4 hover:underline"
                    >
                      contact@heiliaochihua.example
                    </a>
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line"></i>前往联系页面
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}